type SparklineProps = {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
  area?: boolean;
  target?: number | null;
  strokeWidth?: number;
  dot?: boolean;
  id: string;
};

export function Sparkline(props: SparklineProps) {
  const width = () => props.width ?? 120;
  const height = () => props.height ?? 34;
  const color = () => props.color ?? "var(--accent)";
  const strokeWidth = () => props.strokeWidth ?? 1.75;
  const dot = () => props.dot ?? true;
  const area = () => props.area ?? true;

  if (props.data.length === 0) {
    return (
      <svg
        width={width()}
        height={height()}
        viewBox={`0 0 ${width()} ${height()}`}
        class="sparkline"
        aria-hidden="true"
      >
        <line
          x1="3"
          y1={height() / 2}
          x2={width() - 3}
          y2={height() / 2}
          stroke="var(--nodata)"
          stroke-width="1.5"
          stroke-dasharray="3 4"
        />
      </svg>
    );
  }

  const pad = 3;
  const min = Math.min(...props.data);
  const max = Math.max(...props.data);
  const range = max - min || 1;
  const xAt = (index: number) =>
    props.data.length === 1
      ? width() / 2
      : pad + (index / (props.data.length - 1)) * (width() - pad * 2);
  const yAt = (value: number) =>
    height() - pad - ((value - min) / range) * (height() - pad * 2);
  const points = props.data.map(
    (value, index) => [xAt(index), yAt(value)] as const,
  );
  const line = points
    .map((point, index) => {
      if (index === 0) {
        return `M ${point[0].toFixed(1)} ${point[1].toFixed(1)}`;
      }
      const previous = points[index - 1];
      if (!previous) {
        return "";
      }
      const controlX = (previous[0] + point[0]) / 2;
      return `C ${controlX.toFixed(1)} ${previous[1].toFixed(1)} ${controlX.toFixed(1)} ${point[1].toFixed(1)} ${point[0].toFixed(1)} ${point[1].toFixed(1)}`;
    })
    .join(" ");
  const first = points[0];
  const last = points.at(-1);
  const gradientId = `spark-${props.id}`;

  return (
    <svg
      width={width()}
      height={height()}
      viewBox={`0 0 ${width()} ${height()}`}
      class="sparkline"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color={color()} stop-opacity="0.18" />
          <stop offset="100%" stop-color={color()} stop-opacity="0" />
        </linearGradient>
      </defs>
      {props.target !== null && props.target !== undefined ? (
        <line
          x1="0"
          y1={yAt(props.target)}
          x2={width()}
          y2={yAt(props.target)}
          stroke="var(--text-3)"
          stroke-width="1"
          stroke-dasharray="2 3"
          opacity="0.7"
        />
      ) : null}
      {area() && first && last ? (
        <path
          d={`${line} L ${last[0].toFixed(1)} ${height()} L ${first[0].toFixed(1)} ${height()} Z`}
          fill={`url(#${gradientId})`}
        />
      ) : null}
      <path
        d={line}
        fill="none"
        stroke={color()}
        stroke-width={strokeWidth()}
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      {dot() && last ? (
        <circle
          cx={last[0]}
          cy={last[1]}
          r={strokeWidth() + 1}
          fill={color()}
        />
      ) : null}
    </svg>
  );
}
